import type { NextPage } from "next";
import dynamic from "next/dynamic";
import React, { useState, useEffect } from "react";
import { useRouter } from "next/router";
import {
  doc,
  getDoc,
  collection,
  setDoc,
  updateDoc,
} from "@firebase/firestore";
import { db } from "../../utils/firebase";
import { NodeModule } from "../../components/NodeModule";
import { EdgeModule } from "../../components/EdgeModule";
import { GraphModule } from "../../components/GraphInfoModule";
import { AddNodeModule } from "../../components/AddNodeModule";

const ForceGraph3D = dynamic(() => import("react-force-graph-3d"), {
  ssr: false,
});

type ProjectType = {
  name: string;
  img: string;
  description: string;
  public: boolean;
  owner: string;
  id: string;
  version: number;
  permissions?: string[];
};

let colors = ["blue", "white"];

let colorsDictionary: any = {
  0: "#f44336",
  1: "#e91e63",
  2: "#9c27b0",
  3: "#673ab7",
  4: "#3f51b5",
  5: "#2196f3",
  6: "#03a9f4",
  7: "#00bcd4",
  8: "#009688",
  10: "#4caf50",
  11: "#8bc34a",
  12: "#cddc39",
  13: "#ffeb3b",
  14: "#ffc107",
  15: "#ff9800",
  16: "#ff5722",
  17: "#01579b",
  18: "#bf360c",
  19: "#f06292",
  20: "#4dd0e1",
};

const GraphPage: NextPage = (projectInfo: any) => {
  const [graphData, setGraphData] = useState<any>();
  const [formatedData, setFormatedData] = useState<any>();
  const [projectData, setProjectData] = useState<ProjectType>({
    name: "",
    img: "",
    description: "",
    public: true,
    owner: "",
    id: "",
    version: 0,
    permissions: [],
  });
  const [nodeOfInterest, setNodeOfInterest] = useState<any>(null);
  const [graphStatistics, setGraphStatistics] = useState<any>({
    "Average Connections": 0,
    "Total Nodes": 0,
    "Total Edges": 0,
    "Total Clusters": 0,
  });
  const [edgeOutboundConnections, setEdgeOutboundConnections] = useState<any>(
    []
  );
  const [edgeInboundConnections, setEdgeInboundConnections] = useState<any>([]);
  const [edgeOfInterest, setEdgeOfInterest] = useState<any>(null);
  // Information, Add Node, Specific Node

  const [modalOption, setModalOption] = useState("General Information");
  const [nodeToAdd, setNodeToAdd] = useState<any>({
    name: "Sponge Bob",
    label: "Red",
    id: "Sponge Bob",
    shape: "circularImage",
    image:
      "https://firebasestorage.googleapis.com/v0/b/rlhub-4e357.appspot.com/o/unityAI.png?alt=media&token=ae341976-b8e1-4963-a209-d4b4fcda246d",
    color: "red",
    links: [],
    description: "",
  });
  const hasWindow = typeof window !== "undefined";
  const history = useRouter();

  useEffect(() => {
    if (hasWindow) {
      const f = function (): any {
        setWindowDimensions(getWindowDimensions());
      };

      window.addEventListener("resize", f());
      return () => window.removeEventListener("resize", f());
    }
  }, [hasWindow]);

  useEffect(() => {
    ///TODO: Get User Data and check if they are allowed to see project
    // if user and user in persmission db
    setTimeout(getProjectData, 1000);

    // Run every userchange
  }, []);

  useEffect(() => {
    if (graphData !== undefined) {
      setGraphStatistics({
        ...graphStatistics,
        "Average Connections":
          getNetworkStatistics(graphData)["Average Connections"],
        "Total Nodes": getNetworkStatistics(graphData)["Total Nodes"],
        "Total Edges": getNetworkStatistics(graphData)["Total Edges"],
      });
    }
  }, [graphData]);

  //draw a circle to a canvas and return
  function drawCircle() {
    if (typeof document === "undefined") {
      // during server evaluation
    } else {
      let canvas: any = document.createElement("canvas");
      let img: any = document.getElementById("testImage");

      let pat;
      if (img !== null) {
        canvas.id = "canvas";
        canvas.width = 100;
        canvas.height = 100;
        let ctx = canvas.getContext("2d");
        pat = ctx.createPattern(img, "no-repeat");
        ctx.rect(0, 0, 100, 100);
        ctx.fillStyle = pat;
        ctx.fill();
      } else {
        canvas.id = "canvas";
        canvas.width = 32;
        canvas.height = 32;
        let ctx = canvas.getContext("2d");
        let PI2 = Math.PI * 2;
        ctx.arc(16, 16, 16, 0, PI2, true);
        pat = colors[Math.floor(Math.random() * colors.length)];
        ctx.fillStyle = pat;
        ctx.fill();
      }

      return canvas;
    }
  }

  function getWindowDimensions() {
    if (hasWindow !== undefined) {
      const width = hasWindow ? window.innerWidth : null;
      const height = hasWindow ? window.innerHeight : null;
      return {
        width,
        height,
      };
    }
  }

  const populateNodeInfo = (nodeInformation: any, fullGraphData: any) => {
    //List of connected Nodes (Ids only) Outbound
    let edgeDictionaryOutbound: any = {};
    let edgeListOutbound: string[] = [];

    //List of connected Nodes (Ids only) Inbound
    let edgeDictionaryInbound: any = {};
    let edgeListInbound: string[] = [];

    ///Get Connected Edges Ids from Edge List
    for (let i = 0; i < fullGraphData.links.length; i++) {
      if (fullGraphData.links[i].source.id === nodeInformation?.id) {
        edgeDictionaryOutbound[fullGraphData.links[i].target.id] = true;
      }
      if (fullGraphData.links[i].target.id === nodeInformation?.id) {
        edgeDictionaryInbound[fullGraphData.links[i].source.id] = true;
      }
    }

    //Add Node Information to EdgeList in State
    for (let i = 0; i < fullGraphData.nodes.length; i++) {
      if (fullGraphData.nodes[i].id in edgeDictionaryOutbound) {
        edgeListOutbound.push(fullGraphData.nodes[i]);
      }
      if (fullGraphData.nodes[i].id in edgeDictionaryInbound) {
        edgeListInbound.push(fullGraphData.nodes[i]);
      }
    }

    //Set Node Info
    setNodeOfInterest(nodeInformation);
    setEdgeOutboundConnections(edgeListOutbound);
    setEdgeInboundConnections(edgeListInbound);
    setModalOption("Specific Node");
  };

  const createThousands = () => {
    let nodesArray: any = graphData["nodes"];
    let edgesArray: any = graphData["links"];

    for (let i = 1; i < 10; i++) {
      nodesArray.push({
        id: nodesArray.length,
        index: nodesArray.length,
        canvas: drawCircle(),
        description: "",
        name: "",
        label: "",
        group: Math.ceil(Math.random() * 10),
      });
    }

    setGraphData({
      ...graphData,
      nodes: nodesArray,
    });

    for (let i = 1; i < 20; i++) {
      let sourceN = nodesArray[Math.ceil(Math.random() * nodesArray.length)];
      let targetN =
        nodesArray[Math.ceil(Math.random() * nodesArray.length - 1)];

      if (
        sourceN?.index !== undefined &&
        targetN?.index !== undefined &&
        (sourceN.index !== 2 || targetN.index !== 2)
      ) {
        edgesArray.push({
          source: sourceN.index,
          target: targetN.index,
        });
      }
    }

    setGraphData({
      ...graphData,

      link: edgesArray,
    });
  };

  const getGroupColours = (graphData: any) => {
    let numberOfEdges: number = graphData.links?.length || 0;
    let edgesArray: any = graphData.links;
    let nodeArray: any = graphData.nodes;
    let adjancencyMatrix: any = {};
    let visitedDictionary: any = {};
    let numberOfGroups: number = 0;

    colorsDictionary[1];
    function DFS(x: number) {
      visitedDictionary[x] = true;

      nodeArray[x]["group"] = numberOfGroups;
      nodeArray[x]["color"] = colorsDictionary[numberOfGroups % 20];

      let neighbors = adjancencyMatrix[x];
      if (neighbors !== undefined) {
        for (const connections of neighbors) {
          if (!(connections in visitedDictionary)) {
            DFS(parseInt(connections));
          }
        }
      }
    }

    for (let i = 0; i < numberOfEdges; i++) {
      let source: any = edgesArray[i].source.id;
      let target: any = edgesArray[i].target.id;

      if (source != undefined && target != undefined) {
        if (source in adjancencyMatrix) {
          adjancencyMatrix[source] = adjancencyMatrix[source].concat([
            parseInt(target),
          ]);
        } else {
          adjancencyMatrix[source] = [parseInt(target)];
        }
      }
    }

    for (const num in Object.keys(adjancencyMatrix)) {
      if (!(parseInt(num) in visitedDictionary)) {
        numberOfGroups = numberOfGroups + 1;
        DFS(parseInt(num));
      }
    }

    setGraphStatistics({
      ...graphStatistics,
      "Total Clusters": numberOfGroups,
    });
    setGraphData({ ...graphData, nodes: nodeArray });
  };

  const getNetworkStatistics = (graphData: any) => {
    let numberOfNodes: number = graphData.nodes?.length || 0;
    let numberOfEdges: number = graphData.links?.length || 0;

    let edgeDictionary: any = {};
    ///Get Connected Edges Ids from Edge List
    for (let i = 0; i < graphData.links.length; i++) {
      if (graphData.links[i]?.source.id in edgeDictionary) {
        edgeDictionary[graphData.links[i]?.source.id] =
          edgeDictionary[graphData.links[i]?.source.id] + 1;
      } else {
        edgeDictionary[graphData.links[i]?.source.id] = 1;
      }
    }

    let sumValues: any = Object.values(edgeDictionary).reduce(
      (a: any, b: any) => a + b,
      0
    );

    return {
      "Average Connections": sumValues / numberOfNodes,
      "Total Edges": numberOfEdges,
      "Total Nodes": numberOfNodes,
    };
  };

  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions()
  );

  //TODO:Clean this and check Stoage Differnece
  const getProjectData = async () => {
    let graphData;
    let projectGeneralInfo: any;

    const docRef = doc(db, "projects", projectInfo.projectInfo);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      projectGeneralInfo = docSnap.data();
    } else {
      alert("No document found");
      return history.push("/projects");
    }

    setProjectData(projectGeneralInfo);

    const docRefData = doc(db, "datasets", projectInfo.projectInfo);
    const docSnapData = await getDoc(docRefData);

    if (docSnapData.exists()) {
      graphData = docSnapData.data();
      for (let node = 0; node < graphData.nodes.length; node++) {
        graphData.nodes[node].canvas = drawCircle();
      }
    } else {
      alert("No document found");

      return history.push("/projects");
    }

    for (let node = 0; node < graphData.nodes.length; node++) {
      graphData.nodes[node].canvas = drawCircle();
    }
    setGraphData(graphData);
  };

  const saveNodeData = async (dataToSave: any) => {
    alert(
      "This is disabled as I am currently porting graph data to a new server/project"
    );
    return;
    let edgeData = [];
    let nodeList = [];

    for (let edge = 0; edge < dataToSave.links.length; edge++) {
      edgeData.push({
        source: dataToSave.links[edge].source.id,
        target: dataToSave.links[edge].target.id,
      });
    }

    for (let node = 0; node < dataToSave.nodes.length; node++) {
      nodeList.push({
        id: dataToSave.nodes[node].id,
        index: dataToSave.nodes[node].id,
        img: "https://firebasestorage.googleapis.com/v0/b/kadi-90781.appspot.com/o/graph%2Faws.png?alt=media&token=0fce822f-68db-4351-82de-a93922d51254",
        description: dataToSave.nodes[node].description || "",
        name: dataToSave.nodes[node].name || "",
        label: dataToSave.nodes[node].label || "",
        history: [],
        canvas:
          "https://firebasestorage.googleapis.com/v0/b/kadi-90781.appspot.com/o/graph%2Faws.png?alt=media&token=0fce822f-68db-4351-82de-a93922d51254",
      });
    }

    if (nodeList.length > 50 || edgeData.length > 50) {
      alert(
        "Can't save this on the cloud. But we saved it in your local machine"
      );
      window.localStorage.setItem(
        projectInfo.projectInfo,
        JSON.stringify({
          nodes: nodeList,
          links: edgeData,
        })
      );
    } else {
      updateDoc(doc(db, "projects", projectInfo.projectInfo), {
        version: projectData.version + 0.0001,
      })
        .then(() => console.log("Saved Data"))
        .catch((err) => console.log(err));

      return await setDoc(
        doc(collection(db, "datasets"), projectInfo.projectInfo),
        {
          nodes: nodeList,
          links: edgeData,
          version: projectData.version + 0.0001,
        }
      )
        .then(() => alert("Saved Data"))
        .catch((err) => console.log(err));
    }
  };

  return (
    <div className=" bg-black">
      <p className="w-full bg-white text-black p-2 text-center text-sm">
        {" "}
        In the process of updating and migrating this project to a Postgress SQL
        hosted on Planetscale to improve page performance and enable live
        rendering of images inside nodes :) Will also improve UI
      </p>
      <div className="grid grid-cols-3">
        <div className="col-span-1">
          <p
            className="text-white t ml-6 mt-2 hover:cursor-pointer hover:text-slate-200"
            onClick={() => history.push("/")}
          >
            {"o<>o"}
          </p>

          <div className="max-w-sm rounded overflow-hidden shadow-lg bg-white m-5">
            {nodeOfInterest && modalOption === "Specific Node" && (
              <NodeModule
                nodeOfInterest={nodeOfInterest}
                graphData={graphData}
                setNodeOfInterest={setNodeOfInterest}
                edgeOutboundConnections={edgeOutboundConnections}
                edgeInboundConnections={edgeInboundConnections}
                populateNodeInfo={populateNodeInfo}
                setGraphData={setGraphData}
                setModalOption={setModalOption}
                graphStatistics={graphStatistics}
              />
            )}
            {edgeOfInterest !== null && modalOption === "Specific Edge" && (
              <EdgeModule
                edgeOfInterest={edgeOfInterest}
                setEdgeOfInterest={setEdgeOfInterest}
                graphData={graphData}
                setGraphData={setGraphData}
                setModalOption={setModalOption}
              />
            )}

            {modalOption === "General Information" &&
              projectData !== undefined && (
                <div>
                  <GraphModule
                    graphStatistics={graphStatistics}
                    setModalOption={setModalOption}
                    projectData={projectData}
                  />
                  <div className="m-4">
                    <button
                      className="bg-blue-600 my-1 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full"
                      onClick={() => history.push("/projects")}
                    >
                      {" "}
                      Back to Projects
                    </button>
                    <button
                      className="bg-blue-600 my-1  hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline  w-full"
                      onClick={() => saveNodeData(graphData)}
                    >
                      {" "}
                      Save Progress
                    </button>
                  </div>
                </div>
              )}
            {modalOption === "Add Node" && (
              <AddNodeModule
                setModalOption={setModalOption}
                setNodeToAdd={setNodeToAdd}
                graphData={graphData}
                nodeToAdd={nodeToAdd}
                drawCircle={drawCircle}
                setGraphData={setGraphData}
              />
            )}
          </div>
          <button
            className="text-white rounded w-2/3 bg-blue-600 cursor-pointer ml-4 p-1 my-2 hover:bg-blue-500"
            onClick={() => {
              getGroupColours(graphData);
            }}
          >
            {" "}
            Perform DCF to get Cluster Groups
          </button>
          <button
            className="text-white rounded w-2/3 bg-blue-600 cursor-pointer ml-4 p-1 hover:bg-blue-500"
            onClick={() => {
              createThousands();
            }}
          >
            {" "}
            Add 10 Random Nodes
          </button>
        </div>
        <div className="col-span-2">
          {graphData !== null && (
            <ForceGraph3D
              graphData={graphData}
              width={
                windowDimensions?.width ? windowDimensions.width * 0.66 : 10
              }
              onNodeClick={(e: any) => populateNodeInfo(e, graphData)}
              onLinkClick={(e: any) => {
                setEdgeOfInterest(e);
                setModalOption("Specific Edge");
              }}
              linkOpacity={0.5}
              linkDirectionalArrowLength={3.5}
              linkDirectionalArrowRelPos={1}
              nodeAutoColorBy={"group"}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export async function getServerSideProps(context: any) {
  let projectInfo: string = context.query.id;

  return {
    props: { projectInfo }, // will be passed to the page component as props
  };
}

export default GraphPage;
